import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  cabMeetingsQuery,
  createCabMeeting,
  type CabMeetingList,
  type MeetingForm,
  type MeetingScope,
} from '../api/cabMeetings'
import { ApiError } from '../api/client'
import { LoadFailed } from '../ui/LoadFailed'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import './CabMeetings.css'

/**
 * Change management → CAB meetings: lista sedintelor CAB si sedinta noua.
 * Ecran nou, numai in v4.
 *
 * Sedinta are data, loc, grupul CAB (cu membrii lui ca participanti, daca nu se
 * aleg altii) si o agenda de change-uri; agenda, hotararile si minuta se scriu
 * pe pagina sedintei (`CabMeetingPage`).
 */

const SCOPES: { value: MeetingScope; label: string }[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All' },
]

export function failedToast(error: unknown) {
  toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
}

export function when(value: string): string {
  return value === '' ? '-' : value.replace('T', ' ')
}

export function CabMeetingsPage() {
  const [scope, setScope] = useState<MeetingScope>('upcoming')
  const [adding, setAdding] = useState(false)
  const list = useQuery(cabMeetingsQuery(scope))
  const navigate = useNavigate()

  if (list.error instanceof ApiError && list.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (list.error) return <LoadFailed what="CAB meetings" error={list.error} onRetry={() => void list.refetch()} />
  if (!list.data) return <Spinner />

  const open = (id: number) => void navigate({ to: '/easeedesk/cab_meetings/$id', params: { id: String(id) } })

  return (
    <div className="od-cab-meetings">
      {list.data.canEdit && (
        <div className="pull-right">
          <button type="button" className="btn btn-success btn-sm od-cab-meeting-add" onClick={() => setAdding(true)}>
            <i className="fas fa-plus" aria-hidden="true"></i> New CAB meeting
          </button>
        </div>
      )}
      <h4>
        <strong>CAB</strong> meetings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        When the Change Advisory Board meets, which changes are on the agenda, and what it decided. Decisions are
        written in the minutes; a CAB member can also cast them as their vote on the change.
      </label>
      <hr />

      {adding && <NewMeeting page={list.data} onCancel={() => setAdding(false)} onCreated={open} />}

      <div className="btn-group od-cab-scope" role="group" style={{ marginBottom: 10 }}>
        {SCOPES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={'btn btn-default btn-sm' + (scope === s.value ? ' active' : '')}
            aria-pressed={scope === s.value}
            onClick={() => setScope(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {list.data.meetings.length === 0 ? (
        <p className="color-light-grey od-cab-meetings-empty">No CAB meeting here.</p>
      ) : (
        <div className="od-cab-table-wrap">
          <table className="table table-hover od-cab-meetings-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Meeting</th>
                <th>CAB group</th>
                <th>Agenda</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {list.data.meetings.map((m) => (
                <tr key={m.ID} className="od-cab-meeting-row" onClick={() => open(m.ID)} style={{ cursor: 'pointer' }}>
                  <td style={{ whiteSpace: 'nowrap' }}>{when(m.meeting_at)}</td>
                  <td>
                    <a
                      href={`/app/easeedesk/cab_meetings/${m.ID}`}
                      onClick={(e) => {
                        e.preventDefault()
                        open(m.ID)
                      }}
                    >
                      <strong>{m.title}</strong>
                    </a>
                    {m.location !== '' && <div className="color-light-grey">{m.location}</div>}
                  </td>
                  <td>{m.cab_group_name || '-'}</td>
                  <td>
                    {m.items === 0 ? 'empty' : `${m.decided} of ${m.items} decided`}
                  </td>
                  <td>
                    <span className={'label od-cab-status-' + m.status}>{m.status_label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function NewMeeting({ page, onCancel, onCreated }: { page: CabMeetingList; onCancel(): void; onCreated(id: number): void }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<MeetingForm>({ title: '', meeting_at: '', location: '', cab_group_id: 0, participants: [] })

  const save = useMutation({
    mutationFn: () => createCabMeeting(form),
    onSuccess: (answer) => {
      toast(answer.message, 'CAB meetings')
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'cab_meetings'] })
      if (answer.id) onCreated(answer.id)
    },
    onError: failedToast,
  })

  const toggle = (id: number) =>
    setForm({
      ...form,
      participants: form.participants.includes(id) ? form.participants.filter((p) => p !== id) : [...form.participants, id],
    })

  return (
    <form
      className="od-cab-meeting-form well well-sm"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <div className="row">
        <div className="col-md-5 form-group">
          <label htmlFor="od-cab-title">Title</label>
          <input
            id="od-cab-title"
            className="form-control"
            maxLength={150}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>
        <div className="col-md-3 form-group">
          <label htmlFor="od-cab-when">Date and time</label>
          <input
            id="od-cab-when"
            type="datetime-local"
            className="form-control"
            value={form.meeting_at}
            onChange={(e) => setForm({ ...form, meeting_at: e.target.value })}
          />
        </div>
        <div className="col-md-4 form-group">
          <label htmlFor="od-cab-where">Location</label>
          <input
            id="od-cab-where"
            className="form-control"
            maxLength={255}
            value={form.location}
            placeholder="Room, or the link of the call"
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="od-cab-group">CAB group</label>
        <select
          id="od-cab-group"
          className="form-control"
          value={form.cab_group_id === 0 ? '' : String(form.cab_group_id)}
          onChange={(e) => setForm({ ...form, cab_group_id: Number(e.target.value) || 0 })}
        >
          <option value="">None</option>
          {page.groups.map((g) => (
            <option key={g.ID} value={g.ID}>
              {g.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Participants</label>
        <div className="color-light-grey">Leave empty to invite the members of the CAB group.</div>
        <div className="od-cab-people">
          {page.staff.map((p) => (
            <label key={p.ID} className="checkbox-inline">
              <input type="checkbox" checked={form.participants.includes(p.ID)} onChange={() => toggle(p.ID)} /> {p.name}
            </label>
          ))}
        </div>
      </div>
      <button type="submit" className="btn btn-primary btn-sm od-cab-meeting-save" disabled={save.isPending}>
        Create
      </button>{' '}
      <button type="button" className="btn btn-default btn-sm" onClick={onCancel}>
        Cancel
      </button>
    </form>
  )
}
